import i18n from 'src/i18n'
import { DELIVERY_LOCATION_TYPES } from 'src/models/delivery-location/DeliveryLocation'
import { DELIVERY_TYPE_OPTIONS } from 'src/models/delivery-type/DeliveryType'
import { CONDITIONS as CATEGORY_CONDITIONS, CONDITIONS_REQUIREMENTS as CATEGORY_CONDITIONS_REQUIREMENTS, CONDITIONS_REQUIREMENTS } from 'src/models/item/Category'
import { IsoDate } from 'src/models/model'
import { TYPES as SALES_CHANNEL_TYPES } from 'src/models/model/concerns/SalesChannelRestrictable'
import { AVAILABILITY_REASONS, useAvailabilityResult } from 'src/modules/checkout/composables/sales-availability/AvailabilityResult'
import { useIsItemPackageAvailable } from 'src/modules/checkout/composables/sales-availability/item-packages/IsItemPackageAvailable'
import { useIsItemAvailable } from 'src/modules/checkout/composables/sales-availability/items/IsItemAvailable'
import { toValue } from 'vue'
// Options is a hash containing fields that can be used to validate category availability.
const DEFAULT_OPTIONS = Object.freeze({
  salesChannel: null,
  order: null,
  date: null,
  time: null,
  deliveryTypes: null,
  deliveryLocation: null,
  showingBadges: null,
  showing: null
})

export function useIsCategoryAvailable () {
  const { result } = useAvailabilityResult()
  const { isItemAvailable } = useIsItemAvailable()
  const { isItemPackageAvailable } = useIsItemPackageAvailable()

  function isCategoryAvailable (category, { salesChannel, order, date, time, deliveryTypes, showingBadges, showing, deliveryLocation } = DEFAULT_OPTIONS) {
    const categoryValue = toValue(category)

    if (_.isNil(categoryValue)) {
      return result({
        availableReason: AVAILABILITY_REASONS.IS_NIL,
        availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_NIL}`)
      })
    }

    const salesChannelValue = toValue(salesChannel)
    const orderValue = toValue(order)

    // The Category must be active (not archived) to be presented
    if (categoryValue.archivable && !categoryValue.isActive) {
      return result({
        availableReason: AVAILABILITY_REASONS.IS_ARCHIVED,
        availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_ARCHIVED}`)
      })
    }

    // The Category must be available on the current Sales Channel
    if (!categoryValue.isSalesChannelEnabled(salesChannelValue)) {
      return result({
        availableReason: AVAILABILITY_REASONS.IS_DISABLED_ON_SALES_CHANNEL,
        availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_DISABLED_ON_SALES_CHANNEL}`, { salesChannel: _.startCase(salesChannelValue) })
      })
    }

    if (_.includes(SALES_CHANNEL_TYPES.PRIVATE, salesChannelValue)) {
      return result({
        isAvailable: true
      })
    }

    if (categoryValue.conditions[CATEGORY_CONDITIONS.DELIVERY_TYPES]) {
      // If order is not provided, use deliveryTypes from options
      const deliveryTypesValue = _.isNil(orderValue) ? toValue(deliveryTypes) : _.map(orderValue?.deliveryTypeOptions, 'value')

      if (categoryValue.isDeliveryTypeMatch(
        deliveryTypesValue,
        categoryValue.conditionsRequirements[CATEGORY_CONDITIONS_REQUIREMENTS.DELIVERY_TYPES],
        categoryValue.conditionsRequirements[CATEGORY_CONDITIONS_REQUIREMENTS.EXCLUDE_DELIVERY_TYPES]
      )) {
        return result({
          availableReason: AVAILABILITY_REASONS.IS_NOT_AVAILABLE_FOR_DELIVERY_TYPE,
          availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_NOT_AVAILABLE_FOR_DELIVERY_TYPE}`)
        })
      }
    }

    // The Category must be available for the User
    // E.g. User meets any configured Membership restrictions (e.g. Membership Type, Tier, etc.)
    if (!categoryValue.availableForUser(orderValue?.user)) {
      return result({
        availableReason: AVAILABILITY_REASONS.IS_NOT_AVAILABLE_FOR_USER,
        availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_NOT_AVAILABLE_FOR_USER}`)
      })
    }

    // currentShowing is the Showing currently in context
    // If there is no currentShowing, nextShowing is assumed to be the most relevant Showing
    const applicableShowing = _.isNil(orderValue) ? toValue(showing) : (orderValue?.currentShowing || orderValue?.nextShowing)

    // TODO OOS INDY-6757 applicableShowing should be injected into the Composable using wider contextual state
    // e.g. We don't want Website Orders to permit Showing-specific promotional categories,
    // but we do want to resolve to such a Showing for QR Code Orders
    // Relying on the Order model to maintain contextual application data is fragile/unreliable

    // If there is an applicable Showing, the Category must be available for that Showing
    // If there is no applicable Showing, the Category must be available now
    if (!_.isNil(applicableShowing)) {
      if (categoryValue.conditions[CATEGORY_CONDITIONS.DATE_RANGE]) {
        const dateTime = applicableShowing.siteTime.getTime()
        const isDateRangeMatchValue = categoryValue.isDateRangeMatch(
          dateTime,
          categoryValue.conditionsRequirements[CONDITIONS_REQUIREMENTS.DATE_RANGE_START],
          categoryValue.conditionsRequirements[CONDITIONS_REQUIREMENTS.DATE_RANGE_END],
          categoryValue.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_DATE_RANGE]
        )
        if (isDateRangeMatchValue) {
          return result({
            availableReason: AVAILABILITY_REASONS.IS_NOT_AVAILABLE_FOR_SHOWING_DATE,
            availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_NOT_AVAILABLE_FOR_SHOWING_DATE}`)
          })
        }
      }

      if (categoryValue.conditions[CATEGORY_CONDITIONS.TIME_OF_DAY]) {
        const siteTime = categoryValue.configuration.currentSite.siteTime
        // We want to use the showing time by default
        // If the showing time is in the past, we assume they are ordering in person (i.e. during the showing)
        // In that case, we care if the category is available now, not when the showing started
        const time = applicableShowing.siteTime.getTime() < siteTime.getTime() ? siteTime : applicableShowing.siteTime

        const hour = time.getHours()
        const minute = time.getMinutes()

        const isTimeOfDayMatchValue = categoryValue.isTimeOfDayMatch(
          hour,
          minute,
          categoryValue.conditionsRequirements[CONDITIONS_REQUIREMENTS.TIME_OF_DAY_START],
          categoryValue.conditionsRequirements[CONDITIONS_REQUIREMENTS.TIME_OF_DAY_END],
          categoryValue.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_TIME_OF_DAY]
        )
        if (isTimeOfDayMatchValue) {
          return result({
            availableReason: AVAILABILITY_REASONS.IS_NOT_AVAILABLE_FOR_SHOWING_TIME,
            availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_NOT_AVAILABLE_FOR_SHOWING_TIME}`)
          })
        }
      }

      if (categoryValue.conditions[CATEGORY_CONDITIONS.SHOWING_BADGES]) {
        if (categoryValue.isShowingMatch(
          applicableShowing.showingBadgeIds,
          categoryValue.showingBadgeIds,
          categoryValue.conditionsRequirements[CONDITIONS_REQUIREMENTS.MATCH_SHOWING_BADGES],
          categoryValue.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_SHOWING_BADGES]
        )) {
          return result({
            availableReason: AVAILABILITY_REASONS.IS_NOT_AVAILABLE_FOR_SHOWING_BADGES,
            availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_NOT_AVAILABLE_FOR_SHOWING_BADGES}`)
          })
        }
      }
    } else {
      // Extract availableNow logic to here. Note availableNow is currently utilised by availableForShowings
      if (categoryValue.conditions[CATEGORY_CONDITIONS.DATE_RANGE]) {
        const siteTime = !_.isNil(date) ? IsoDate.parse(toValue(date), 'YYYY-MM-DD') : categoryValue.configuration.currentSite.siteTime

        const dateTime = siteTime.getTime()
        const isDateRangeMatchValue = categoryValue.isDateRangeMatch(
          dateTime,
          categoryValue.conditionsRequirements[CONDITIONS_REQUIREMENTS.DATE_RANGE_START],
          categoryValue.conditionsRequirements[CONDITIONS_REQUIREMENTS.DATE_RANGE_END],
          categoryValue.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_DATE_RANGE]
        )
        if (isDateRangeMatchValue) {
          return result({
            availableReason: AVAILABILITY_REASONS.IS_NOT_AVAILABLE_FOR_DATE,
            availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_NOT_AVAILABLE_FOR_DATE}`)
          })
        }
      }

      if (categoryValue.conditions[CATEGORY_CONDITIONS.TIME_OF_DAY]) {
        const siteTime = categoryValue.configuration.currentSite.siteTime
        const timeValue = toValue(time)

        const [hour, minute] = !_.isNil(timeValue)
          ? _.map(timeValue.split(':'), Number)
          : [siteTime.getHours(), siteTime.getMinutes()]

        const isTimeOfDayMatchValue = categoryValue.isTimeOfDayMatch(
          hour,
          minute,
          categoryValue.conditionsRequirements[CONDITIONS_REQUIREMENTS.TIME_OF_DAY_START],
          categoryValue.conditionsRequirements[CONDITIONS_REQUIREMENTS.TIME_OF_DAY_END],
          categoryValue.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_TIME_OF_DAY]
        )

        if (isTimeOfDayMatchValue) {
          return result({
            availableReason: AVAILABILITY_REASONS.IS_NOT_AVAILABLE_FOR_TIME,
            availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_NOT_AVAILABLE_FOR_TIME}`)
          })
        }
      }

      // TODO OOS INDY-6757 Specific validation logic should be extracted to a specialized Function/Composable
      if (categoryValue.conditions[CATEGORY_CONDITIONS.SHOWING_BADGES]) {
        const deliveryTypeValue = _.isNil(orderValue) ? DELIVERY_LOCATION_TYPES.THEATER_SEAT : orderValue?.deliveryType
        const deliveryLocationValue = _.isNil(orderValue) ? toValue(deliveryLocation) : orderValue?.deliveryLocation

        if (deliveryTypeValue !== DELIVERY_TYPE_OPTIONS.THEATER_SEAT && _.isNil(deliveryLocationValue)) {
          // If there is no applicable Showing or Delivery Location, an INCLUSIVE Showing Badge condition cannot be met, as there are no Showing Badges
          if (!categoryValue.conditionsRequirements[CATEGORY_CONDITIONS_REQUIREMENTS.EXCLUDE_SHOWING_BADGES]) {
            return result({
              availableReason: AVAILABILITY_REASONS.IS_NOT_AVAILABLE_FOR_SHOWING_BADGES,
              availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_NOT_AVAILABLE_FOR_SHOWING_BADGES}`)
            })
          }
        } else {
          // TODO OOS INDY-6757 Relevant configuration should be injected into the Composable
          const foundDeliveryLocation = _.find(categoryValue.configuration.deliveryLocations.data, { name: deliveryLocationValue, type: DELIVERY_LOCATION_TYPES.THEATER_SEAT })
          const showingBadgesValue = _.isNil(foundDeliveryLocation) ? toValue(showingBadges) : foundDeliveryLocation?.screen?.computedShowingBadges

          if (_.isEmpty(showingBadgesValue)) {
            return result({
              availableReason: AVAILABILITY_REASONS.IS_NOT_AVAILABLE_FOR_SHOWING_BADGES,
              availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_NOT_AVAILABLE_FOR_SHOWING_BADGES}`)
            })
          }

          const showingBadgeIds = _.map(showingBadgesValue, 'id')
          if (categoryValue.isShowingMatch(
            showingBadgeIds,
            categoryValue.showingBadgeIds,
            categoryValue.conditionsRequirements[CATEGORY_CONDITIONS_REQUIREMENTS.MATCH_SHOWING_BADGES],
            categoryValue.conditionsRequirements[CATEGORY_CONDITIONS_REQUIREMENTS.EXCLUDE_SHOWING_BADGES]
          )) {
            return result({
              availableReason: AVAILABILITY_REASONS.IS_NOT_AVAILABLE_FOR_SHOWING_BADGES,
              availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_NOT_AVAILABLE_FOR_SHOWING_BADGES}`)
            })
          }
        }
      }
    }

    // NOTE: we want would to refactor and pass in the remaining options to the isItemAvailable and isItemPackageAvailable composables and have them validate against them as well
    const options = {
      salesChannel: salesChannelValue,
      order: orderValue
    }
    const isAnyItemAvailable = _.some(categoryValue.items.data, item => isItemAvailable(item, options).isAvailable)
    // We include isAnyItemAvailable to avoid iterating over itemPackages when they are irrelevant to overall availability
    const isAnyItemPackageAvailable = isAnyItemAvailable || _.some(categoryValue.itemPackages.data, itemPackage => isItemPackageAvailable(itemPackage, options).isAvailable)

    if (!isAnyItemAvailable && !isAnyItemPackageAvailable) {
      return result({
        availableReason: AVAILABILITY_REASONS.IS_SELECTION_REQUIREMENTS_UNMET,
        availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_SELECTION_REQUIREMENTS_UNMET}`)
      })
    }

    return result({
      isAvailable: true
    })
  }

  return {
    isCategoryAvailable
  }
}
